import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

const densityConfig = {
  compact: { cell: 'py-2 px-3 text-[13px]', header: 'py-2 px-3' },
  default: { cell: 'py-3 px-4 text-sm', header: 'py-3 px-4' },
  comfortable: { cell: 'py-4 px-5 text-sm', header: 'py-4 px-5' },
};

function AdvancedDataTable({
  columns = [],
  data = [],
  totalItems = 0,
  loading = false,
  sortBy = '',
  sortOrder = 'desc',
  onSort,
  currentPage = 1,
  pageSize = 25,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100],
  onRowClick,
  rowKey = 'id',
  density = 'compact',
  borderRadius = 'lg',
  stickyHeader = true,
  emptyState,
  entityName = 'items',
  minHeight = 400,
  className = '',
  onColumnResize,
}) {
  const [columnWidths, setColumnWidths] = useState(() =>
    Object.fromEntries(columns.map((col) => [col.key, col.width || 150]))
  );
  const resizingRef = useRef(null);
  const tableRef = useRef(null);

  const styles = densityConfig[density] || densityConfig.compact;
  const totalPages = Math.ceil(totalItems / pageSize);

  // Reset column widths when columns change (but not during a resize)
  useEffect(() => {
    if (resizingRef.current) return;
    setColumnWidths(Object.fromEntries(columns.map((col) => [col.key, col.width || 150])));
  }, [columns]);

  // Column resize handlers
  const lastWidthRef = useRef(null);
  const handleResizeStart = useCallback((e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = columnWidths[colKey] || 150;

    const col = columns.find((c) => c.key === colKey);
    const minWidth = col?.minWidth || 80;

    const handleMouseMove = (moveEvent) => {
      const diff = moveEvent.clientX - startX;
      const newWidth = Math.max(minWidth, startWidth + diff);
      lastWidthRef.current = newWidth;
      setColumnWidths((prev) => ({ ...prev, [colKey]: newWidth }));
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (lastWidthRef.current != null && onColumnResize) {
        onColumnResize(colKey, lastWidthRef.current);
      }
      lastWidthRef.current = null;
      resizingRef.current = null;
      // Suppress the click-to-sort that fires right after mouseup
      justResizedRef.current = true;
      requestAnimationFrame(() => { justResizedRef.current = false; });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    resizingRef.current = colKey;
  }, [columnWidths, columns, onColumnResize]);

  const justResizedRef = useRef(false);
  const handleSort = useCallback((colKey) => {
    if (!onSort || justResizedRef.current) return;
    if (sortBy === colKey) {
      onSort(colKey, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSort(colKey, 'asc');
    }
  }, [onSort, sortBy, sortOrder]);

  const totalWidth = columns.reduce((sum, col) => sum + (columnWidths[col.key] || col.width || 150), 0);

  // Pagination helpers
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [];
    if (currentPage <= 4) {
      pages.push(1, 2, 3, 4, 5, '...', totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }
    return pages;
  };

  // Skeleton loading
  if (loading) {
    return (
      <div className={`border border-gray-200 rounded-${borderRadius} overflow-hidden ${className}`} style={{ minHeight }}>
        <div className="overflow-x-auto scrollbar-hide">
          <table style={{ minWidth: totalWidth }} className="w-full">
            <thead>
              <tr className="bg-white border-b border-gray-200">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`${styles.header} text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}
                    style={{ width: columnWidths[col.key] || col.width || 150 }}
                  >
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="border-b border-gray-100">
                  {columns.map((col) => (
                    <td key={col.key} className={styles.cell}>
                      <div className="h-4 bg-gray-100 rounded animate-pulse" style={{ width: `${50 + Math.random() * 40}%` }} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div className={`border border-gray-200 rounded-${borderRadius} overflow-hidden ${className}`} style={{ minHeight }}>
        {emptyState || (
          <div className="text-center py-16">
            <p className="text-sm text-gray-500">No {entityName} found</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-col border border-gray-200 rounded-${borderRadius} overflow-hidden bg-white ${className}`} style={{ minHeight }}>
      <div className="flex-1 overflow-x-auto scrollbar-hide" ref={tableRef}>
        <table style={{ minWidth: totalWidth }} className="w-full">
          <thead className={stickyHeader ? 'sticky top-0 z-10' : ''}>
            <tr className="bg-white border-b border-gray-200">
              {columns.map((col, colIndex) => {
                const isActive = sortBy === col.key;
                const HeaderIcon = col.headerIcon;
                return (
                  <th
                    key={col.key}
                    className={`${styles.header} text-left text-xs font-medium uppercase tracking-wider relative select-none group ${
                      isActive ? 'text-gray-700 bg-gray-50' : 'text-gray-500'
                    } ${col.sortable && onSort ? 'cursor-pointer hover:bg-gray-50' : ''} ${
                      col.sticky ? 'sticky left-0 z-20 bg-white shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]' : ''
                    }`}
                    style={{ width: columnWidths[col.key] || col.width || 150, minWidth: col.minWidth || 80 }}
                    onClick={() => col.sortable && handleSort(col.key)}
                  >
                    <div className="flex items-center gap-1.5">
                      {HeaderIcon && <HeaderIcon className="w-3.5 h-3.5" />}
                      <span>{col.label}</span>
                      {col.sortable && isActive && (
                        <span className="flex flex-col -space-y-1">
                          {sortOrder === 'asc' ? (
                            <ChevronUp className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5" />
                          )}
                        </span>
                      )}
                    </div>
                    {/* Resize handle */}
                    {colIndex < columns.length - 1 && (
                      <div
                        className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-brand-400 group-hover:bg-gray-300"
                        onMouseDown={(e) => handleResizeStart(e, col.key)}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr
                key={row[rowKey] || rowIndex}
                className={`border-b border-gray-100 transition-colors ${
                  onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''
                }`}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`${styles.cell} text-gray-700 truncate ${
                      col.sticky ? 'sticky left-0 z-[5] bg-white shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]' : ''
                    }`}
                    style={{ width: columnWidths[col.key] || col.width || 150, maxWidth: columnWidths[col.key] || col.width || 150 }}
                  >
                    {col.render ? col.render(row[col.key], row) : (typeof row[col.key] === 'object' && row[col.key] !== null ? (row[col.key].display_name || '\u2014') : (row[col.key] ?? '\u2014'))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      {totalItems > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-white text-[13px]">
          {/* Left: Total count */}
          <div className="text-gray-500">
            Total <span className="font-medium text-gray-700">{totalItems.toLocaleString()}</span> {entityName}
          </div>

          {/* Center: Page numbers */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPageChange?.(currentPage - 1)}
              disabled={currentPage <= 1}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {getPageNumbers().map((page, idx) =>
              page === '...' ? (
                <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">...</span>
              ) : (
                <button
                  key={page}
                  onClick={() => onPageChange?.(page)}
                  className={`min-w-[28px] h-7 rounded text-[13px] font-medium ${
                    page === currentPage
                      ? 'bg-brand-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              )
            )}
            <button
              onClick={() => onPageChange?.(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Right: Page size */}
          {onPageSizeChange && (
            <div className="flex items-center gap-2">
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="border border-gray-200 rounded px-2 py-1 text-[13px] text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                {pageSizeOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt} / page</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdvancedDataTable;
